import {
  Box,
  Button,
  Card,
  CrudMode,
  DataDisplay,
  Grid,
  GridBreak,
  RichTextEditor,
  RichTextReader,
  ValidationErrors,
  newToast,
  pn,
} from "@compass/core";
import { FilePanel, Input, Select } from "components";
import {
  Form,
  FormAnswer,
  FormQuestion,
  FormSection,
  FormSubQuestion,
  SurveyEditResource,
  SurveyViewResource,
  UploadFile,
} from "generated/openApi";

import { CopyLinkButton } from "components/common/CopyLinkButton";
import produce from "immer";
import { useState } from "react";
import ApiService from "services/apiService";
import { initDataModel } from "utils";
import { createEntity } from "utils/CreateEntity";
import { setAppToast } from "redux/reducers/appSlice";
import { useDispatch } from "react-redux";

export const SurveyGeneralInfo = (props: {
  survey: Form;
  setSurvey: (value: Form) => void;
  setCopySurvey: (value: number) => void;
  viewResource: SurveyViewResource;
  editResource: SurveyEditResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
}) => {
  const [linkCopy, setLinkCopy] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [copyExistingSurveyId, setCopyExistingSurveyId] = useState<number>();

  const properties = props.viewResource.formProperties || [];

  const copyExisting = (surveyId: number) => {
    const ignoredSurveyFields = [
      "createdById",
      "createdDate",
      "deactivatedDate",
      "formType",
      "formMode",
      "language",
      "fileId",
      "id",
      "guid",
      "modifiedById",
      "modifiedDate",
      "centerLogoUploadFile",
      "leftLogoUploadFile",
      "rightLogoUploadFile",
    ];
    const ignoredSectionFields = [
      "formId",
      "formQuestions",
      "fileId",
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
    ];
    const ignoredQuestionFields = [
      "fileId",
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
      "formQuestionType",
      "formQuestionWriteInType",
      "formAnswers",
      "formSectionId",
      "formSubQuestions",
      "branchedFromFormAnswerIds",
      "branchToFormQuestionId",
      "branchedFromFormAnswers",
      "branchedFromFormQuestionIds",
      "branchedFromFormQuestions",
      "touched",
    ];
    const ignoredQuestionAnswerFields = [
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
      "fileId",
      "formQuestionId",
      "branchToFormQuestionId",
      "branchToFormQuestionLabel",
      "touched",
    ];
    const ignoredSubQuestionFields = [
      "formQuestionId",
      "createdById",
      "createdDate",
      "id",
      "modifiedById",
      "modifiedDate",
      "fileId",
      "touched",
    ];

    ApiService.surveys.get(surveyId).then((copiedSurvey) => {
      let tempSurvey: Form = createEntity<Form>({});
      for (var field in copiedSurvey) {
        if (!ignoredSurveyFields.includes(field)) tempSurvey[field] = copiedSurvey[field];
      }

      const sectionsCopy: FormSection[] = [];
      // copy sections
      copiedSurvey.formSections?.map((copiedSection) => {
        let tempSection = createEntity<FormSection>({ formQuestions: [], formSubSections: [] });
        for (var field in copiedSection) {
          if (!ignoredSectionFields.includes(field)) tempSection[field] = copiedSection[field];
        }

        sectionsCopy.push(tempSection);
      });
      tempSurvey.formSections = sectionsCopy;

      // copy questions
      const questionsCopy: FormQuestion[] = [];
      copiedSurvey.formQuestions?.map((copiedQuestion) => {
        let tempQuestion = createEntity<FormQuestion>({ formAnswers: [], formSubQuestions: [] });
        for (var field in copiedQuestion) {
          if (!ignoredQuestionFields.includes(field)) tempQuestion[field] = copiedQuestion[field];
        }

        // copy answers
        copiedQuestion.formAnswers?.map((copiedAnswer) => {
          let tempAnswer = createEntity<FormAnswer>({});
          for (var field in copiedAnswer) {
            if (!ignoredQuestionAnswerFields.includes(field)) tempAnswer[field] = copiedAnswer[field];
          }
          tempQuestion.formAnswers?.push(tempAnswer);
        });

        // copy sub-questions
        copiedQuestion.formSubQuestions?.map((copiedSubQuestion) => {
          let tempSubQuestion = createEntity<FormSubQuestion>({});
          for (var field in copiedSubQuestion) {
            if (!ignoredSubQuestionFields.includes(field)) tempSubQuestion[field] = copiedSubQuestion[field];
          }
          tempQuestion.formSubQuestions?.push(tempSubQuestion);
        });

        questionsCopy.push(tempQuestion);
      });
      tempSurvey.formQuestions = questionsCopy;

      props.setSurvey(tempSurvey);
      props.setCopySurvey(surveyId);
    });
  };

  const generateQrCode = () => {
    ApiService.surveys.generateQrCode(props.survey.id).then((response) => {
      props.setSurvey(
        produce(props.survey, (draft) => {
          draft!.qrCodeUploadFile = response;
        })
      );
      dispatch(setAppToast(newToast("success", "QR Code generated and saved successfully.")));
    });
  };

  return (
    <Card header={"Info"}>
      <Grid container spacing="sm">
        <Grid item xs={12}>
          <Grid direction="row" container spacing="lg">
            <Grid item xs={11}>
              {props.survey?.id != 0 && (
                <DataDisplay label="Public Link">{`${props.viewResource?.publicUrl}${props.survey?.guid}/${props.viewResource?.publicTenant}`}</DataDisplay>
              )}
            </Grid>
            <Grid item xs={1}>
              <CopyLinkButton
                copyLink={`${props.viewResource?.publicUrl}${props.survey?.guid}/${props.viewResource?.publicTenant}`}
                showCheck={linkCopy}
                setShowCheck={setLinkCopy}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {
            (props.survey.qrCodeUploadFileId && (
              <FilePanel
                fileName={props.survey.qrCodeUploadFile?.fileName}
                fileSize={props.survey.qrCodeUploadFile?.fileSize}
                fileRef={props.survey.qrCodeUploadFile?.fileRef}
                imagePreviewData={props.survey.qrCodeUploadFile?.imagePreviewData}
              />
            ))
          }
          {
            ((!props.survey.qrCodeUploadFileId && props.crudMode === "edit") && (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Button variant="contained" onClick={generateQrCode}>
                  Generate QR Code
                </Button>
              </Box>
            ))
          }
        </Grid>

        {props.crudMode === "edit" && props.survey?.id == 0 && (
          <Grid item xs={6}>
            <Select
              label="Copy From Existing"
              value={props.viewResource.existingSurveys?.find((x) => x.id === copyExistingSurveyId)}
              options={props.viewResource.existingSurveys}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) => {
                setCopyExistingSurveyId(value?.id);
                copyExisting(value?.id);
              }}
            />
          </Grid>
        )}

        <Grid item lg={3} md={4} xs={12}>
          {props.crudMode === "view" && (
            <DataDisplay label={properties.find((x) => x.propertyName === pn<Form>("formTypeId"))?.displayName}>
              {props.survey.formType?.name}
            </DataDisplay>
          )}
          {props.crudMode === "edit" && (
            <Select
              label={properties.find((x) => x.propertyName === pn<Form>("formTypeId"))?.displayName}
              modelProperty={properties.find((x) => x.propertyName === pn<Form>("formTypeId"))}
              errors={props.validationErrors[pn<Form>("formTypeId")]}
              value={props.editResource.formTypes?.find((x) => x.id === props.survey.formTypeId)}
              options={props.editResource.formTypes}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.formTypeId = value?.id;
                  })
                )
              }
            />
          )}
        </Grid>

        <GridBreak />

        <Grid item lg={3} md={4} xs={12}>
          {props.crudMode === "view" && (
            <DataDisplay label={properties.find((x) => x.propertyName === pn<Form>("name"))?.displayName}>
              {props.survey?.name}
            </DataDisplay>
          )}
          {props.crudMode === "edit" && (
            <Input
              label={properties.find((x) => x.propertyName === pn<Form>("name"))?.displayName}
              modelProperty={properties.find((x) => x.propertyName === pn<Form>("name"))}
              errors={props.validationErrors[pn<Form>("name")]}
              value={props.survey?.name}
              onChange={(e) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.name = e.target.value;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item lg={3} md={4} xs={12}>
          {props.crudMode === "view" && (
            <DataDisplay label={properties.find((x) => x.propertyName === pn<Form>("languageId"))?.displayName}>
              {props.survey.language?.name}
            </DataDisplay>
          )}
          {props.crudMode === "edit" && (
            <Select
              label={properties.find((x) => x.propertyName === pn<Form>("languageId"))?.displayName}
              modelProperty={properties.find((x) => x.propertyName === pn<Form>("languageId"))}
              errors={props.validationErrors[pn<Form>("languageId")]}
              value={props.editResource.languages?.find((x) => x.id === props.survey.languageId)}
              options={props.editResource.languages}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.languageId = value?.id;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item lg={3} md={4} xs={12}>
          {props.crudMode === "view" && (
            <DataDisplay label={properties.find((x) => x.propertyName === pn<Form>("formModeId"))?.displayName}>
              {props.survey.formMode?.name}
            </DataDisplay>
          )}
          {props.crudMode === "edit" && (
            <Select
              label={properties.find((x) => x.propertyName === pn<Form>("formModeId"))?.displayName}
              modelProperty={properties.find((x) => x.propertyName === pn<Form>("formModeId"))}
              errors={props.validationErrors[pn<Form>("formModeId")]}
              value={props.editResource.formModes?.find((x) => x.id === props.survey.formModeId)}
              options={props.editResource.formModes}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.formModeId = value?.id;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item xs={3}>
          {props.crudMode === "view" && (
            <DataDisplay label="Color Theme">{props.survey.themeColor}</DataDisplay>
          )}
          {props.crudMode === "edit" && (
            <Input
            label="Color Theme"
            errors={props.validationErrors[pn<Form>("themeColor")]}
            value={props.survey?.themeColor}
            onChange={(e) =>
              props.setSurvey(
                produce(props.survey, (draft) => {
                  draft!.themeColor = e.target.value;
                })
              )
            }
          />
          )}
        </Grid>

        <GridBreak />

        <Grid item lg={3} md={4} xs={12}>
          {props.crudMode === "view" && (
            <RichTextReader
              label={properties.find((x) => x.propertyName === pn<Form>("title"))?.displayName}
              html={props.survey.title}
            />
          )}
          {props.crudMode === "edit" && (
            <RichTextEditor
              label={properties.find((x) => x.propertyName === pn<Form>("title"))?.displayName}
              hasColor
              hasAlign
              value={props.survey.title}
              onChange={(value) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.title = value!;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item lg={3} md={4} xs={12}>
          {props.crudMode === "view" && (
            <RichTextReader
              label={properties.find((x) => x.propertyName === pn<Form>("subTitle"))?.displayName}
              html={props.survey.subTitle}
            />
          )}
          {props.crudMode === "edit" && (
            <RichTextEditor
              label={properties.find((x) => x.propertyName === pn<Form>("subTitle"))?.displayName}
              hasColor
              hasAlign
              value={props.survey.subTitle}
              onChange={(value) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.subTitle = value;
                  })
                )
              }
            />
          )}
        </Grid>

        <GridBreak />

        <Grid item xs={12}>
          {props.crudMode === "view" && (
            <RichTextReader
              label={properties.find((x) => x.propertyName === pn<Form>("instruction"))?.displayName}
              html={props.survey.instruction}
            />
          )}
          {props.crudMode === "edit" && (
            <RichTextEditor
              label={properties.find((x) => x.propertyName === pn<Form>("instruction"))?.displayName}
              hasColor
              hasAlign
              value={props.survey.instruction}
              onChange={(value) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.instruction = value!;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item xs={12}>
          {props.crudMode === "view" && (
            <DataDisplay
              label={properties.find((x) => x.propertyName === pn<Form>("emailTemplate"))?.displayName}
              type="textarea"
            >
              {props.survey.emailTemplate}
            </DataDisplay>
          )}
          {props.crudMode === "edit" && (
            <Input
              multiline
              label={properties.find((x) => x.propertyName === pn<Form>("emailTemplate"))?.displayName}
              required={properties.find((x) => x.propertyName === pn<Form>("emailTemplate"))?.required}
              maxLength={properties.find((x) => x.propertyName === pn<Form>("emailTemplate"))?.maxLength}
              errors={props.validationErrors[pn<Form>("emailTemplate")]}
              value={props.survey.emailTemplate}
              onChange={(e) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.emailTemplate = e.target.value;
                  })
                )
              }
            />
          )}
        </Grid>

        <Grid item xs={12}>
          {props.crudMode === "view" && (
            <RichTextReader
              label={properties.find((x) => x.propertyName === pn<Form>("submitMessage"))?.displayName}
              html={props.survey.submitMessage}
            />
          )}
          {props.crudMode === "edit" && (
            <RichTextEditor
              label={properties.find((x) => x.propertyName === pn<Form>("submitMessage"))?.displayName}
              hasColor
              hasAlign
              value={props.survey.submitMessage}
              onChange={(value) =>
                props.setSurvey(
                  produce(props.survey, (draft) => {
                    draft!.submitMessage = value;
                  })
                )
              }
            />
          )}
        </Grid>

        <GridBreak />

        <Grid item md={4} xs={12}>
          <FilePanel
            label={properties.find((x) => x.propertyName === pn<Form>("leftLogoUploadFileId"))?.displayName}
            fileName={props.survey?.leftLogoUploadFile?.fileName}
            fileRef={props.survey?.leftLogoUploadFile?.fileRef}
            fileSize={props.survey?.leftLogoUploadFile?.fileSize}
            uploadingData={props.survey?.leftLogoUploadFile?.uploadingData}
            imagePreviewData={props.survey?.leftLogoUploadFile?.imagePreviewData}
            onFileChange={(fileName, fileSize, uploadingData) =>
              props.setSurvey(
                produce(props.survey, (draft) => {
                  draft!.leftLogoUploadFile = initDataModel<UploadFile>({
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                  });
                })
              )
            }
            cancel={() =>
              props.setSurvey(
                produce(props.survey, (draft) => {
                  draft!.leftLogoUploadFile = undefined;
                  draft!.leftLogoUploadFileId = undefined;
                })
              )
            }
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <FilePanel
            label={properties.find((x) => x.propertyName === pn<Form>("centerLogoUploadFileId"))?.displayName}
            fileName={props.survey?.centerLogoUploadFile?.fileName}
            fileRef={props.survey?.centerLogoUploadFile?.fileRef}
            fileSize={props.survey?.centerLogoUploadFile?.fileSize}
            uploadingData={props.survey?.centerLogoUploadFile?.uploadingData}
            imagePreviewData={props.survey?.centerLogoUploadFile?.imagePreviewData}
            onFileChange={(fileName, fileSize, uploadingData) =>
              props.setSurvey(
                produce(props.survey, (draft) => {
                  draft!.centerLogoUploadFile = initDataModel<UploadFile>({
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                  });
                })
              )
            }
            cancel={() =>
              props.setSurvey(
                produce(props.survey, (draft) => {
                  draft!.centerLogoUploadFile = undefined;
                  draft!.centerLogoUploadFileId = undefined;
                })
              )
            }
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <FilePanel
            label={properties.find((x) => x.propertyName === pn<Form>("rightLogoUploadFileId"))?.displayName}
            fileName={props.survey?.rightLogoUploadFile?.fileName}
            fileRef={props.survey?.rightLogoUploadFile?.fileRef}
            fileSize={props.survey?.rightLogoUploadFile?.fileSize}
            uploadingData={props.survey?.rightLogoUploadFile?.uploadingData}
            imagePreviewData={props.survey?.rightLogoUploadFile?.imagePreviewData}
            onFileChange={(fileName, fileSize, uploadingData) =>
              props.setSurvey(
                produce(props.survey, (draft) => {
                  draft!.rightLogoUploadFile = initDataModel<UploadFile>({
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                  });
                })
              )
            }
            cancel={() =>
              props.setSurvey(
                produce(props.survey, (draft) => {
                  draft!.rightLogoUploadFile = undefined;
                  draft!.rightLogoUploadFileId = undefined;
                })
              )
            }
          />
        </Grid>
      </Grid>
    </Card>
  );
};
